import React, {useContext, useEffect, useState} from 'react';
import {Redirect, Route, Switch} from 'react-router-dom';
import Notifications from 'shared/components/notifications';
import browserHistory from 'shared/history';
import CollegeSearch from 'src/components/CollegeSearch/CollegeSearch';
import {LoadingRecommendationRow} from 'src/components/CollegeSearch/LoadingRecommendation';
import MyList from 'src/components/CollegeSearch/MyList';
import Recommendations from 'src/components/CollegeSearch/RecommendationBuckets';
import Sticky from 'src/components/Sticky';
import {AppLayoutContext} from 'src/components/app-layout';
import RoutedTabs from 'src/components/routed-tabs';
import {withPlatformContext, WithPlatformContextProps} from 'src/hooks/usePlatform';
import useUserType from 'src/hooks/use-user-type/use-user-type';
import {RecommendationsService} from 'src/services/RecommendationsService';
import 'stylesheets/components/college-search/college-search-bar.scss';
import 'stylesheets/components/college-search/college-search-tab.scss';
import {useMediaQuery} from '../../../hooks/useMediaQuery';
import {useBoolean} from '../../../hooks/useBoolean';

interface Tab {
  label: string;
  path: string;
}

const tabConfig: Record<string, Tab> = {
  recommendations: {
    label: 'RECOMMENDATIONS',
    path: '/recommendations',
  },
  search: {
    label: 'SEARCH COLLEGES',
    path: '/search',
  },
  myList: {
    label: 'MY LIST',
    path: '/my-list',
  },
};

interface Props extends WithPlatformContextProps {
  initializeCollegeSearch: (userType: string) => void;
  isSenior?: boolean;
}

const CollegeSearchComponent = () => <CollegeSearch />;

const TabbedCollegeSearch = ({initializeCollegeSearch, isSenior, platformContext}: Props) => {
  const {userType, isHighSchool, isTransfer} = useUserType();
  const {topToolbarHeight} = useContext(AppLayoutContext);
  const recommendationsOnline = useBoolean(false);
  const recommendationStatusLoaded = useBoolean(false);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const mq = useMediaQuery();

  useEffect(() => {
    initializeCollegeSearch(userType);
    RecommendationsService.getState().then((serviceState) => {
      if (!serviceState.disableRecommendations) {
        recommendationsOnline.setTrue();
      }
      recommendationStatusLoaded.setTrue();
    });
  }, []);

  useEffect(() => {
    setTabs(
      [
        !mq.MS_AND_DOWN && tabConfig.myList,
        !isTransfer && recommendationsOnline.value && tabConfig.recommendations,
        tabConfig.search,
      ].filter((tab): tab is Tab => !!tab)
    );
  }, [mq.MS_AND_DOWN, recommendationsOnline.value, isTransfer]);

  const isOnMobileMyList = mq.MS_AND_DOWN && browserHistory.location.pathname.includes('my-list');

  const showRoutedTabs = !(isOnMobileMyList || (mq.MS_AND_DOWN && isTransfer));
  const showRecommendations = recommendationsOnline.value && !isTransfer;

  let tab;
  if (isSenior) {
    tab = tabConfig.myList;
  } else if (showRecommendations) {
    tab = tabConfig.recommendations;
  } else {
    tab = tabConfig.search;
  }

  // wait until we've loaded the status of recommendations before rendering the routes
  if (!recommendationStatusLoaded.value) {
    return (
      <div className="college-search-tab">
        <Notifications />
        <LoadingRecommendationRow rowCount={5} />
      </div>
    );
  }

  const redirectPath = `/colleges${tab.path}`;

  return (
    <div className="college-search-tab">
      {showRoutedTabs && (
        <Sticky top={platformContext.isIonic ? 0 : topToolbarHeight} innerZ={3}>
          <RoutedTabs
            tabs={tabs}
            basePath="/colleges"
            className={
              isHighSchool && platformContext.isIonic && mq.MS_AND_DOWN
                ? 'college-search-mobile --invert'
                : undefined
            }
          />
        </Sticky>
      )}
      <Switch>
        <Route path="/colleges/my-list" component={MyList} />
        <Route path="/colleges/search" component={CollegeSearchComponent} />
        {showRecommendations && (
          <Route path="/colleges/recommendations" component={Recommendations} />
        )}
        <Redirect exact from="/colleges" to={redirectPath} />
      </Switch>

      <Notifications />
    </div>
  );
};

export default withPlatformContext(TabbedCollegeSearch);
